import { useFlagsmithLoading } from 'flagsmith/react';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export type SecureRouteAccess = {
  mayUseVerifiedRoutes: boolean;
  mayUsePrimumSettings: boolean;
  mayUseFellowsCorner: boolean;
};

/**
 * Which guarded secure screens the reader may reach, shared by every navigator that declares
 * one: the secure layout's stack and each tab's stack. Two copies of these rules would be two
 * answers for the same screen depending on which stack it was pushed on.
 *
 * Each answer stays **open** while it is not yet known. A guard does not redirect. It removes
 * the screen's name from its navigator, and the router falls back to that navigator's first
 * screen. So a guard that reads "no" before its data arrives does not delay a reader, it
 * relocates one, on an answer nobody had yet. Staying open on unknown is safe because none of
 * these is the enforcement boundary: the secure layout's redirects are, and the API is.
 *
 * - **Verified routes** read `!needsNpiVerification`, not a direct ability check. The two
 *   disagree whenever the current-user query has not produced a user, and the stricter one
 *   reads false there, closing every guard because a request failed.
 * - **Developer settings** treat group membership as unknown until the groups query settles for
 *   this reader. A query that has never run is not loading and not an answer either, and
 *   reading it as "not staff" would drop the route under a staff member. It also needs
 *   verification, so staff tooling is not the one route an unverified reader can still reach.
 * - **Fellows Corner** is reachable by direct link, so the flag is enforced on the route
 *   rather than only on its entrances. Once flags settle an unresolved one stays off, so an
 *   outage closes the feature rather than opening it.
 */
export function useSecureRouteAccess(): SecureRouteAccess {
  const { needsNpiVerification } = useAuthStatus();

  const { isPrimumUser, isGroupMembershipResolved } = useCognitoGroups();

  const isFellowsCornerEnabled = useFeatureFlag(FEATURE_FLAGS.FELLOWS_CORNER);

  const flagsmithLoading = useFlagsmithLoading();

  const haveFlagsSettled = flagsmithLoading != null && !flagsmithLoading.isLoading;

  const mayUseVerifiedRoutes = !needsNpiVerification;

  return {
    mayUseVerifiedRoutes,
    mayUsePrimumSettings: mayUseVerifiedRoutes && (!isGroupMembershipResolved || isPrimumUser),
    mayUseFellowsCorner: mayUseVerifiedRoutes && (!haveFlagsSettled || isFellowsCornerEnabled),
  };
}
